import { ref, reactive, computed, onMounted, onUnmounted } from "@vue/composition-api";

import {
  mdiEmailSync,
  mdiLinkVariant,
  mdiGoogleSpreadsheet,
  mdiSync,
  mdiSendClock,
  mdiCalendarClock,
  mdiHistory,
  mdiChevronUp,
  mdiChevronDown,
  mdiSend,
  mdiAccount,
  mdiAccountGroup,
  mdiPlus,
  mdiMagnify,
  mdiPencil,
  mdiDelete,
  mdiMapMarkerPath,
  mdiAlertCircle,
  mdiContentCopy,
  mdiEye,
  mdiEyeOff,
  mdiEyeOutline,
  mdiCheckCircle,
  mdiCancel,
  mdiCheck,
  mdiClose,
  mdiRobot,
} from "@mdi/js";
import autoEmailService from "@/services/autoEmailReport.service";
import { useGssSync } from "./useGssSync";
import { RMR_DANGER, RMR_INDIGO, RMR_SUCCESS, RMR_WARNING } from "@/styles/tokens";

export function useActorsPerimeter() {
  const colors = {
    indigo: RMR_INDIGO,
    indigoBg: "#EEF2FF",
    danger: RMR_DANGER,
    success: RMR_SUCCESS,
    warning: RMR_WARNING,
  };

  const icons = {
    mdiEmailSync,
    mdiLinkVariant,
    mdiGoogleSpreadsheet,
    mdiSync,
    mdiSendClock,
    mdiCalendarClock,
    mdiHistory,
    mdiChevronUp,
    mdiChevronDown,
    mdiSend,
    mdiAccount,
    mdiAccountGroup,
    mdiPlus,
    mdiMagnify,
    mdiPencil,
    mdiDelete,
    mdiMapMarkerPath,
    mdiAlertCircle,
    mdiContentCopy,
    mdiEye,
    mdiEyeOff,
    mdiEyeOutline,
    mdiCheckCircle,
    mdiCancel,
    mdiCheck,
    mdiClose,
    mdiRobot,
  };

  const isSystemTriggered = (item) => {
    if (!item) return false;
    const trig = (item.triggered_by || item.user_name || "").toLowerCase();
    return item.action_type === "scheduled" || trig.includes("système") || trig.includes("systeme");
  };

  const loadingConfig = ref(false);
  const loadingHistory = ref(false);
  const historyList = ref([]);
  const loadingGssSyncHistory = ref(false);
  const gssSyncHistoryList = ref([]);
  const showGssSyncHistory = ref(true);

  const loadingActors = ref(false);
  const loadingPerimeters = ref(false);
  const showHistory = ref(true);
  const showActors = ref(true);
  const showPerimeters = ref(true);
  const originalSpreadsheetUrl = ref("");
  const savingSpreadsheetUrl = ref(false);

  const errorDetailsModal = ref(false);
  const selectedErrorItem = ref(null);

  const immediateSendType = ref("draft");
  const triggeringImmediate = ref(false);
  const confirmImmediateModal = ref(false);

  const previewModal = ref(false);
  const loadingPreview = ref(false);
  const previewData = ref(null);
  const selectedPreviewIndex = ref(0);

  const confirmToggleModal = ref(false);
  const pendingScheduledState = ref(false);

  const confirmRattrapageModal = ref(false);
  const selectedRattrapageItem = ref(null);
  const executingRattrapage = ref(false);

  const standaloneRattrapageModal = ref(false);
  const standaloneRattrapageMonth = ref(null);
  const standaloneRattrapageSendType = ref("draft");
  const executingStandaloneRattrapage = ref(false);

  const availableSites = ref([]);

  const dailyQuota = reactive({
    daily_quota_max: 500,
    daily_quota_used: 0,
    daily_quota_remaining: 500,
    is_quota_exceeded: false,
    has_smtp_quota_error: false,
  });

  const config = reactive({
    spreadsheet_url: "",
    send_type: "draft",
    is_scheduled_active: false,
    scheduled_send_type: "draft",
    immediate_draft_recipient_email: "programmatique@reworldmedia.com",
    scheduled_draft_recipient_email: "programmatique@reworldmedia.com",
    selected_site_ids: [],
    last_sync_at: null,
    updated_at: null,
  });

  const selectedSitesImmediate = ref([]);

  const editDraftModal = ref(false);
  const editDraftType = ref("immediate");
  const editDraftEmail = ref("");
  const savingDraftEmail = ref(false);

  const historySitesModal = ref(false);
  const selectedHistorySites = ref([]);

  const openHistorySitesModal = (item) => {
    const siteIds = item.selected_site_ids || [];
    const siteMap = new Map((availableSites.value || []).map((s) => [s.id, s.name]));
    selectedHistorySites.value = siteIds.map((id) => ({
      id,
      name: siteMap.get(id) || `Site #${id}`,
    }));
    historySitesModal.value = true;
  };

  const historyHeaders = [
    { text: "Date Action", value: "sent_at" },
    { text: "Type Action", value: "action_type" },
    { text: "Mode d'envoi", value: "send_type" },
    { text: "Sites inclus", value: "sites", sortable: false },
    { text: "Mois Cible", value: "target_month" },
    { text: "Statut", value: "status" },
    { text: "Actions", value: "actions", sortable: false, align: "right" },
  ];

  const gssSyncHistoryHeaders = [
    { text: "Date d'exécution", value: "created_at" },
    { text: "Déclenché par", value: "triggered_by" },
    { text: "Lien GSS / Spreadsheet", value: "spreadsheet_url", sortable: false },
    { text: "Onglet ciblé", value: "target_tab" },
    { text: "Statut", value: "status" },
  ];



  const actors = ref([]);
  const searchActor = ref("");
  const actorHeaders = [
    { text: "Acteur", value: "name" },
    { text: "Contact Email", value: "email" },
    { text: "Actions", value: "actions", sortable: false, align: "right", width: "120px" },
  ];

  const perimeters = ref([]);
  const searchPerimeter = ref("");
  const perimeterHeaders = [
    { text: "Périmètre", value: "name" },
    { text: "Envoyer à (Acteurs)", value: "actors", sortable: false },
    { text: "Actions", value: "actions", sortable: false, align: "right", width: "120px" },
  ];

  const actorDialog = ref(false);
  const savingActor = ref(false);
  const editedActor = reactive({ id: null, name: "", email: "" });
  const confirmDeleteActorModal = ref(false);
  const actorToDeleteId = ref(null);

  const actorDetailsDialog = ref(false);
  const selectedActorDetail = ref(null);

  const perimeterDialog = ref(false);
  const savingPerimeter = ref(false);
  const editedPerimeter = reactive({ id: null, name: "", site_ids: [], sites_selected: [], actor_ids: [] });
  const confirmDeletePerimeterModal = ref(false);
  const perimeterToDeleteId = ref(null);

  const snackbar = reactive({ show: false, text: "", color: "success" });

  const currentYear = computed(() => new Date().getFullYear());

  const notify = (text, color = "success") => {
    snackbar.text = text;
    snackbar.color = color;
    snackbar.show = true;
  };

  const fetchConfig = async () => {
    loadingConfig.value = true;
    try {
      const res = await autoEmailService.getConfig();
      if (res.data) {
        Object.assign(config, res.data);
        if (!config.selected_site_ids) {
          config.selected_site_ids = [];
        }
        if (res.data.daily_quota) {
          Object.assign(dailyQuota, res.data.daily_quota);
        }
        if (res.data.send_type) {
          immediateSendType.value = res.data.send_type;
        }
        originalSpreadsheetUrl.value = res.data.spreadsheet_url || "";
      }
    } catch (err) {
      notify("Erreur lors de la récupération de la config", "error");
    } finally {
      loadingConfig.value = false;
    }
  };

  const quotaPercentage = computed(() => {
    if (!dailyQuota.daily_quota_max) return 0;
    const pct = (dailyQuota.daily_quota_used / dailyQuota.daily_quota_max) * 100;
    return Math.min(100, Math.max(0, pct));
  });

  const onImmediateSendTypeChange = async (val) => {
    const previousType = config.send_type;
    config.send_type = val;
    try {
      const payload = {
        ...config,
        spreadsheet_url: originalSpreadsheetUrl.value,
      };
      const res = await autoEmailService.updateConfig(payload);
      if (res.data) {
        Object.assign(config, res.data);
        notify("Configuration mise à jour avec succès");
      }
    } catch (err) {
      config.send_type = previousType;
      immediateSendType.value = previousType;
      notify("Erreur lors de l'enregistrement du mode d'envoi", "error");
    }
  };

  const isSpreadsheetUrlDirty = computed(() => {
    return (config.spreadsheet_url || "").trim() !== (originalSpreadsheetUrl.value || "").trim();
  });

  const GOOGLE_SHEET_REGEX = /^https:\/\/(?:docs|drive)\.google\.com\/spreadsheets\/d\/[a-zA-Z0-9_-]+/;

  const saveSpreadsheetUrl = async () => {
    const url = (config.spreadsheet_url || "").trim();
    if (url && !GOOGLE_SHEET_REGEX.test(url)) {
      notify("Le lien doit être une URL valide Google Sheets (https://docs.google.com/spreadsheets/d/...)", "warning");
      return;
    }
    savingSpreadsheetUrl.value = true;
    try {
      await saveConfig();
      originalSpreadsheetUrl.value = config.spreadsheet_url || "";
    } catch (err) {
      notify("Erreur lors de l'enregistrement du lien Spreadsheet", "error");
    } finally {
      savingSpreadsheetUrl.value = false;
    }
  };

  const cancelSpreadsheetUrlEdit = () => {
    config.spreadsheet_url = originalSpreadsheetUrl.value;
  };

  const fetchGssSyncHistory = async () => {
    loadingGssSyncHistory.value = true;
    try {
      const res = await autoEmailService.getGssSyncHistory();
      gssSyncHistoryList.value = res.data || [];
    } catch (err) {
      console.error("Erreur chargement historique GSS", err);
    } finally {
      loadingGssSyncHistory.value = false;
    }
  };

  const { syncingSpreadsheet, syncSpreadsheetData: executeSync, stopSyncPolling } = useGssSync(
    notify,
    async () => {
      await fetchConfig();
      await fetchHistory();
      await fetchGssSyncHistory();
    },
    async () => {
      await fetchGssSyncHistory();
    },
    async () => {
      await fetchGssSyncHistory();
    },
    async () => {
      await fetchGssSyncHistory();
    }
  );

  const confirmSyncModal = ref(false);

  const openConfirmSyncModal = () => {
    confirmSyncModal.value = true;
  };

  const syncSpreadsheetData = async () => {
    await executeSync(config.spreadsheet_url);
    await fetchGssSyncHistory();
  };

  const executeConfirmSync = async () => {
    confirmSyncModal.value = false;
    await syncSpreadsheetData();
  };


  const siteSearchImmediate = ref("");
  const siteSearchScheduled = ref("");

  const filteredSites = computed(() => {
    if (!perimeters.value || perimeters.value.length === 0) return [];
    const perimeterSiteIds = new Set();
    perimeters.value.forEach((p) => {
      if (p.is_active !== false && p.site_ids) {
        p.site_ids.forEach((sid) => perimeterSiteIds.add(sid));
      }
    });
    return availableSites.value.filter((s) => perimeterSiteIds.has(s.id));
  });

  const filteredSitesImmediateList = computed(() => {
    const search = (siteSearchImmediate.value || "").toLowerCase().trim();
    if (!search) return filteredSites.value;
    return filteredSites.value.filter((s) => (s.name || "").toLowerCase().includes(search));
  });

  const filteredSitesScheduledList = computed(() => {
    const search = (siteSearchScheduled.value || "").toLowerCase().trim();
    if (!search) return filteredSites.value;
    return filteredSites.value.filter((s) => (s.name || "").toLowerCase().includes(search));
  });

  const fetchSites = async () => {
    try {
      const res = await autoEmailService.getSites();
      availableSites.value = res.data || [];
      if (selectedSitesImmediate.value.length === 0 && filteredSites.value.length > 0) {
        selectedSitesImmediate.value = filteredSites.value.map((s) => s.id);
      }
      if (config.selected_site_ids === null && filteredSites.value.length > 0) {
        config.selected_site_ids = filteredSites.value.map((s) => s.id);
      }
    } catch (err) {
      console.error("Erreur chargement des sites", err);
    }
  };

  const fetchActors = async () => {
    loadingActors.value = true;
    try {
      const res = await autoEmailService.getActors();
      actors.value = res.data || [];
    } catch (err) {
      notify("Erreur chargement acteurs", "error");
    } finally {
      loadingActors.value = false;
    }
  };

  const fetchPerimeters = async () => {
    loadingPerimeters.value = true;
    try {
      const res = await autoEmailService.getPerimeters();
      perimeters.value = res.data || [];
    } catch (err) {
      notify("Erreur chargement périmètres", "error");
    } finally {
      loadingPerimeters.value = false;
    }
  };

  let pollingTimer = null;
  let isPollingInFlight = false;
  let pollingFailures = 0;
  let isPollingActive = false;
  const MAX_POLLING_FAILURES = 5;

  const startPolling = () => {
    isPollingActive = true;
    if (!pollingTimer) {
      pollingTimer = setTimeout(() => {
        pollingTimer = null;
        if (isPollingActive) fetchHistory(true);
      }, 5000);
    }
  };

  const stopPolling = () => {
    isPollingActive = false;
    if (pollingTimer) {
      clearTimeout(pollingTimer);
      pollingTimer = null;
    }
    pollingFailures = 0;
  };

  const fetchHistory = async (isPoll = false) => {
    if (isPoll && isPollingInFlight) return;
    if (isPoll) isPollingInFlight = true;
    if (!isPoll) loadingHistory.value = true;
    try {
      const res = await autoEmailService.getHistory();
      pollingFailures = 0;
      const newHistory = res.data || [];
      
      const wasPending = historyList.value.some(
        (item) => item.status === "in_progress" || item.status === "pending"
      );
      historyList.value = newHistory;
      const isNowPending = historyList.value.some(
        (item) => item.status === "in_progress" || item.status === "pending"
      );

      if (wasPending && !isNowPending) {
        fetchConfig();
      }

      if (isNowPending) {
        startPolling();
      } else {
        stopPolling();
      }
    } catch (err) {
      if (isPoll) {
        pollingFailures++;
        if (pollingFailures >= MAX_POLLING_FAILURES) {
          stopPolling();
          notify("Polling interrompu en raison d'erreurs réseau répétées", "warning");
        } else if (isPollingActive) {
          startPolling();
        }
      } else {
        notify("Erreur chargement historique", "error");
      }
    } finally {
      if (isPoll) isPollingInFlight = false;
      if (!isPoll) loadingHistory.value = false;
    }
  };

  const toggleAllImmediateSites = () => {
    if (selectedSitesImmediate.value.length === filteredSites.value.length) {
      selectedSitesImmediate.value = [];
    } else {
      selectedSitesImmediate.value = filteredSites.value.map((s) => s.id);
    }
  };

  const toggleAllScheduledSites = () => {
    const current = config.selected_site_ids || [];
    if (current.length === filteredSites.value.length) {
      config.selected_site_ids = [];
    } else {
      config.selected_site_ids = filteredSites.value.map((s) => s.id);
    }
    saveConfig();
  };

  const fetchData = async () => {
    await Promise.all([
      fetchConfig(),
      fetchActors(),
      fetchPerimeters(),
      fetchHistory(),
      fetchGssSyncHistory(),
    ]);
    await fetchSites();
  };


  onMounted(() => {
    fetchData();
  });

  onUnmounted(() => {
    stopPolling();
    stopSyncPolling();
  });

  const saveConfig = async () => {
    try {
      const payload = {
        ...config,
      };
      const res = await autoEmailService.updateConfig(payload);
      if (res.data) {
        Object.assign(config, res.data);
        notify("Configuration mise à jour avec succès");
      }
    } catch (err) {
      notify("Erreur lors de l'enregistrement de la config", "error");
    }
  };

  const openPreviewModal = async () => {
    if (!selectedSitesImmediate.value || selectedSitesImmediate.value.length === 0) {
      notify("Veuillez sélectionner au moins un site pour afficher l'aperçu", "warning");
      return;
    }
    loadingPreview.value = true;
    try {
      const res = await autoEmailService.previewReports({
        sendType: immediateSendType.value,
        actionType: "immediate",
        isMonthlyRattrapage: false,
        selectedSiteIds: selectedSitesImmediate.value,
      });
      if (res && res.data && res.data.previews && res.data.previews.length > 0) {
        previewData.value = res.data;
        selectedPreviewIndex.value = 0;
        previewModal.value = true;
      } else {
        notify("Aucune donnée disponible pour générer l'aperçu", "warning");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur lors de la génération de l'aperçu";
      notify(msg, "error");
    } finally {
      loadingPreview.value = false;
    }
  };

  const currentPreviewReport = computed(() => {
    if (!previewData.value || !previewData.value.previews || previewData.value.previews.length === 0) {
      return null;
    }
    const idx = Math.max(0, Math.min(selectedPreviewIndex.value, previewData.value.previews.length - 1));
    return previewData.value.previews[idx];
  });

  const openImmediateModal = () => {
    if (!selectedSitesImmediate.value || selectedSitesImmediate.value.length === 0) {
      notify("Veuillez sélectionner au moins un site pour l'envoi immédiat", "warning");
      return;
    }
    confirmImmediateModal.value = true;
  };

  const executeImmediateSend = async () => {
    if (!selectedSitesImmediate.value || selectedSitesImmediate.value.length === 0) {
      notify("Veuillez sélectionner au moins un site pour l'envoi immédiat", "warning");
      return;
    }
    triggeringImmediate.value = true;
    try {
      await autoEmailService.triggerSend({
        sendType: immediateSendType.value,
        actionType: "immediate",
        isMonthlyRattrapage: false,
        selectedSiteIds: selectedSitesImmediate.value,
      });
      notify("Envoi du rapport initié en arrière-plan ! Le statut sera mis à jour dans l'historique.");
      confirmImmediateModal.value = false;
      previewModal.value = false;
      await fetchHistory();
    } catch (err) {
      notify("Erreur lors du déclenchement du rapport immédiat", "error");
    } finally {
      triggeringImmediate.value = false;
    }
  };

  const requestToggleScheduled = (newVal) => {
    pendingScheduledState.value = !!newVal;
    confirmToggleModal.value = true;
  };

  const cancelScheduledToggle = () => {
    config.is_scheduled_active = !pendingScheduledState.value;
    confirmToggleModal.value = false;
  };

  const executeScheduledToggle = async () => {
    try {
      config.is_scheduled_active = pendingScheduledState.value;
      await saveConfig();
      notify(
        `Programmation automatique ${config.is_scheduled_active ? "activée" : "désactivée"} avec succès !`
      );
      confirmToggleModal.value = false;
    } catch (err) {
      config.is_scheduled_active = !pendingScheduledState.value;
      notify("Erreur lors de la mise à jour de la planification", "error");
    }
  };

  const openRattrapageModal = (item) => {
    selectedRattrapageItem.value = item;
    confirmRattrapageModal.value = true;
  };

  const openEditDraftModal = (type) => {
    editDraftType.value = type;
    editDraftEmail.value = type === "immediate"
      ? (config.immediate_draft_recipient_email || "programmatique@reworldmedia.com")
      : (config.scheduled_draft_recipient_email || "programmatique@reworldmedia.com");
    editDraftModal.value = true;
  };

  const saveDraftRecipientEmail = async () => {
    if (!editDraftEmail.value || !editDraftEmail.value.includes("@")) {
      notify("Veuillez saisir une adresse email valide", "error");
      return;
    }
    savingDraftEmail.value = true;
    try {
      if (editDraftType.value === "immediate") {
        config.immediate_draft_recipient_email = editDraftEmail.value.trim();
      } else {
        config.scheduled_draft_recipient_email = editDraftEmail.value.trim();
      }
      await saveConfig();
      editDraftModal.value = false;
      notify("Adresse email draft mise à jour avec succès");
    } catch (err) {
      notify("Erreur lors de la mise à jour de l'adresse email", "error");
    } finally {
      savingDraftEmail.value = false;
    }
  };

  const executeRattrapage = async () => {
    if (!selectedRattrapageItem.value) return;
    executingRattrapage.value = true;
    try {
      await autoEmailService.triggerSend({
        targetDate: selectedRattrapageItem.value.target_date,
        sendType: selectedRattrapageItem.value.send_type,
        actionType: "rattrapage",
        isMonthlyRattrapage: true,
        selectedSiteIds: selectedRattrapageItem.value.selected_site_ids,
      });
      notify("Rattrapage initié en arrière-plan !");
      confirmRattrapageModal.value = false;
      await fetchHistory();
    } catch (err) {
      notify("Erreur lors de l'exécution du rattrapage", "error");
    } finally {
      executingRattrapage.value = false;
    }
  };

  const availableRattrapageMonths = computed(() => {
    const months = [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    for (let m = 0; m <= currentMonth; m++) {
      const d = new Date(currentYear, m, 1);
      const yearStr = d.getFullYear();
      const monthStr = String(d.getMonth() + 1).padStart(2, "0");
      const dateVal = `${yearStr}-${monthStr}-01`;

      const monthLabel = d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
      const formattedLabel = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);

      months.push({
        text: formattedLabel,
        value: dateVal,
      });
    }

    return months.reverse();
  });

  const openStandaloneRattrapageModal = () => {
    if (availableRattrapageMonths.value.length > 0) {
      standaloneRattrapageMonth.value = availableRattrapageMonths.value[0].value;
    }
    standaloneRattrapageSendType.value = "draft";
    standaloneRattrapageModal.value = true;
  };

  const executeStandaloneRattrapage = async () => {
    if (!standaloneRattrapageMonth.value) {
      notify("Veuillez sélectionner un mois pour le rattrapage", "warning");
      return;
    }
    executingStandaloneRattrapage.value = true;
    try {
      await autoEmailService.triggerSend({
        targetDate: standaloneRattrapageMonth.value,
        sendType: standaloneRattrapageSendType.value,
        actionType: "rattrapage",
        isMonthlyRattrapage: true,
      });
      notify("Rattrapage du mois initié en arrière-plan !");
      standaloneRattrapageModal.value = false;
      await fetchHistory();

    } catch (err) {
      notify("Erreur lors de l'exécution du rattrapage", "error");
    } finally {
      executingStandaloneRattrapage.value = false;
    }
  };



  const openActorDialog = (item = null) => {
    if (item) {
      editedActor.id = item.id;
      editedActor.name = item.name;
      editedActor.email = item.email;
    } else {
      editedActor.id = null;
      editedActor.name = "";
      editedActor.email = "";
    }
    actorDialog.value = true;
  };

  const openActorDetails = (actor) => {
    selectedActorDetail.value = actor;
    actorDetailsDialog.value = true;
  };

  const getActorPerimeters = (actorId) => {
    if (!actorId) return [];
    return perimeters.value.filter((p) => p.actors && p.actors.some((a) => a.id === actorId));
  };

  const saveActor = async () => {
    if (!editedActor.name || !editedActor.email) {
      notify("Veuillez remplir le nom et l'email", "warning");
      return;
    }
    savingActor.value = true;
    try {
      if (editedActor.id) {
        await autoEmailService.updateActor(editedActor.id, editedActor);
        notify("Acteur modifié avec succès");
      } else {
        await autoEmailService.createActor(editedActor);
        notify("Acteur créé avec succès");
      }
      actorDialog.value = false;
      await fetchActors();
      await fetchPerimeters();
    } catch (err) {
      notify(err.response?.data?.detail || "Erreur lors de l'enregistrement", "error");
    } finally {
      savingActor.value = false;
    }
  };

  const deleteActor = (id) => {
    actorToDeleteId.value = id;
    confirmDeleteActorModal.value = true;
  };

  const executeDeleteActor = async () => {
    if (!actorToDeleteId.value) return;
    try {
      await autoEmailService.deleteActor(actorToDeleteId.value);
      notify("Acteur supprimé");
      confirmDeleteActorModal.value = false;
      await fetchActors();
      await fetchPerimeters();
    } catch (err) {
      notify("Erreur lors de la suppression", "error");
    } finally {
      actorToDeleteId.value = null;
    }
  };

  const openPerimeterDialog = (item = null) => {
    if (item) {
      let matchedSites = [];
      if (item.site_ids && item.site_ids.length > 0) {
        matchedSites = item.site_ids
          .map((id) => availableSites.value.find((s) => s.id === id))
          .filter(Boolean);
      } else if (item.name) {
        const currentSites = item.name.split(",").map((s) => (s.strip ? s.strip() : s.trim()));
        matchedSites = currentSites
          .map((nameStr) => availableSites.value.find((s) => s.name === nameStr || s.code === nameStr) || nameStr)
          .filter(Boolean);
      }

      editedPerimeter.id = item.id;
      editedPerimeter.name = item.name;
      editedPerimeter.site_ids = item.site_ids || matchedSites.map((s) => (typeof s === "object" ? s.id : null)).filter(Boolean);
      editedPerimeter.sites_selected = matchedSites;
      editedPerimeter.actor_ids = item.actors ? item.actors.map((a) => a.id) : [];
    } else {
      editedPerimeter.id = null;
      editedPerimeter.name = "";
      editedPerimeter.site_ids = [];
      editedPerimeter.sites_selected = [];
      editedPerimeter.actor_ids = [];
    }
    perimeterDialog.value = true;
  };

  const onSitesSelectionChange = (val) => {
    if (!val) {
      editedPerimeter.name = "";
      editedPerimeter.site_ids = [];
      editedPerimeter.sites_selected = [];
      return;
    }
    const names = val.map((item) => (typeof item === "object" ? item.name : item));
    editedPerimeter.name = names.join(", ");
    editedPerimeter.site_ids = val
      .map((item) => {
        if (typeof item === "object" && item.id) return item.id;
        const found = availableSites.value.find((s) => s.name === item || s.code === item);
        return found ? found.id : null;
      })
      .filter(Boolean);
  };

  const savePerimeter = async () => {
    if (!editedPerimeter.name) {
      notify("Veuillez renseigner le nom du périmètre", "warning");
      return;
    }
    savingPerimeter.value = true;
    try {
      if ((!editedPerimeter.site_ids || editedPerimeter.site_ids.length === 0) && editedPerimeter.sites_selected.length > 0) {
        editedPerimeter.site_ids = editedPerimeter.sites_selected
          .map((item) => {
            if (typeof item === "object" && item.id) return item.id;
            const found = availableSites.value.find((s) => s.name === item || s.code === item);
            return found ? found.id : null;
          })
          .filter(Boolean);
      }

      const payload = {
        name: editedPerimeter.name,
        site_ids: editedPerimeter.site_ids,
        actor_ids: editedPerimeter.actor_ids,
      };

      if (editedPerimeter.id) {
        await autoEmailService.updatePerimeter(editedPerimeter.id, payload);
        notify("Périmètre modifié avec succès");
      } else {
        await autoEmailService.createPerimeter(payload);
        notify("Périmètre créé avec succès");
      }
      perimeterDialog.value = false;
      await fetchPerimeters();
    } catch (err) {
      notify(err.response?.data?.detail || "Erreur lors de l'enregistrement", "error");
    } finally {
      savingPerimeter.value = false;
    }
  };

  const deletePerimeter = (id) => {
    perimeterToDeleteId.value = id;
    confirmDeletePerimeterModal.value = true;
  };

  const executeDeletePerimeter = async () => {
    if (!perimeterToDeleteId.value) return;
    try {
      await autoEmailService.deletePerimeter(perimeterToDeleteId.value);
      notify("Périmètre supprimé");
      confirmDeletePerimeterModal.value = false;
      await fetchPerimeters();
    } catch (err) {
      notify("Erreur lors de la suppression", "error");
    } finally {
      perimeterToDeleteId.value = null;
    }
  };

  const getInitial = (name) => {
    if (!name) return "?";
    return name.trim().charAt(0).toUpperCase();
  };

  const formatDate = (val) => {
    if (!val) return "";
    const d = new Date(val);
    return d.toLocaleDateString("fr-FR", { year: "numeric", month: "2-digit", day: "2-digit" });
  };

  const formatDateTime = (val) => {
    if (!val) return "-";
    const d = new Date(val);
    return d.toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateMonth = (val) => {
    if (!val) return "-";
    const d = new Date(val);
    const str = d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const openErrorDetailsModal = (item) => {
    selectedErrorItem.value = item;
    errorDetailsModal.value = true;
  };

  const copyErrorText = () => {
    if (!selectedErrorItem.value || !selectedErrorItem.value.error_message) return;
    navigator.clipboard.writeText(selectedErrorItem.value.error_message);
    notify("Message d'erreur copié dans le presse-papier !");
  };

  const getActionTypeLabel = (type) => {
    if (type === "immediate" || type === "Immédiat") return "Immédiat";
    if (type === "scheduled" || type === "planifié" || type === "Planifié") return "Planifié";
    if (type === "rattrapage" || type === "Rattrapage") return "Rattrapage";
    return type || "Immédiat";
  };

  const toggleActorActive = async (actor) => {
    try {
      const updated = !actor.is_active;
      await autoEmailService.updateActor(actor.id, { is_active: updated });
      actor.is_active = updated;
      notify(`Acteur ${actor.name} ${updated ? "activé" : "désactivé"}`);
      await fetchPerimeters();
    } catch (err) {
      notify("Erreur lors de la modification du statut de l'acteur", "error");
    }
  };

  const togglePerimeterActive = async (perimeter) => {
    try {
      const updated = !perimeter.is_active;
      await autoEmailService.updatePerimeter(perimeter.id, { is_active: updated });
      perimeter.is_active = updated;
      notify(`Périmètre ${perimeter.name} ${updated ? "activé" : "désactivé"}`);
    } catch (err) {
      notify("Erreur lors de la modification du statut du périmètre", "error");
    }
  };

  const getActiveActors = (perimeter) => {
    if (!perimeter || !perimeter.actors) return [];
    return perimeter.actors.filter((a) => a.is_active !== false);
  };

  return {
    colors,
    icons,
    loadingConfig,
    loadingHistory,
    loadingActors,
    loadingPerimeters,
    showHistory,
    showActors,
    showPerimeters,
    originalSpreadsheetUrl,
    isSpreadsheetUrlDirty,
    savingSpreadsheetUrl,
    saveSpreadsheetUrl,
    cancelSpreadsheetUrlEdit,
    syncingSpreadsheet,
    syncSpreadsheetData,
    confirmSyncModal,
    openConfirmSyncModal,
    executeConfirmSync,
    errorDetailsModal,
    selectedErrorItem,
    immediateSendType,
    triggeringImmediate,
    confirmImmediateModal,
    confirmToggleModal,
    pendingScheduledState,
    confirmRattrapageModal,
    selectedRattrapageItem,
    executingRattrapage,
    standaloneRattrapageModal,
    standaloneRattrapageMonth,
    standaloneRattrapageSendType,
    executingStandaloneRattrapage,
    availableRattrapageMonths,
    openStandaloneRattrapageModal,
    executeStandaloneRattrapage,
    dailyQuota,
    availableSites,
    filteredSites,
    siteSearchImmediate,
    siteSearchScheduled,
    filteredSitesImmediateList,
    filteredSitesScheduledList,
    selectedSitesImmediate,
    config,
    editDraftModal,
    editDraftType,
    editDraftEmail,
    savingDraftEmail,
    historySitesModal,
    selectedHistorySites,
    openHistorySitesModal,
    historyList,
    historyHeaders,
    gssSyncHistoryList,
    loadingGssSyncHistory,
    showGssSyncHistory,
    gssSyncHistoryHeaders,
    fetchGssSyncHistory,
    actors,

    searchActor,
    actorHeaders,
    perimeters,
    searchPerimeter,
    perimeterHeaders,
    actorDialog,
    savingActor,
    editedActor,
    confirmDeleteActorModal,
    actorToDeleteId,
    actorDetailsDialog,
    selectedActorDetail,
    perimeterDialog,
    savingPerimeter,
    editedPerimeter,
    confirmDeletePerimeterModal,
    perimeterToDeleteId,
    snackbar,
    currentYear,
    quotaPercentage,
    toggleAllImmediateSites,
    toggleAllScheduledSites,
    notify,
    fetchData,
    fetchSites,
    fetchConfig,
    saveConfig,
    openImmediateModal,
    executeImmediateSend,
    previewModal,
    loadingPreview,
    previewData,
    selectedPreviewIndex,
    openPreviewModal,
    currentPreviewReport,
    requestToggleScheduled,
    cancelScheduledToggle,
    executeScheduledToggle,
    openRattrapageModal,
    openEditDraftModal,
    saveDraftRecipientEmail,
    executeRattrapage,
    fetchActors,
    fetchPerimeters,
    fetchHistory,
    openActorDialog,
    openActorDetails,
    getActorPerimeters,
    saveActor,
    deleteActor,
    executeDeleteActor,
    openPerimeterDialog,
    onSitesSelectionChange,
    savePerimeter,
    deletePerimeter,
    executeDeletePerimeter,
    toggleActorActive,
    togglePerimeterActive,
    getActiveActors,
    getInitial,
    formatDate,
    formatDateTime,
    formatDateMonth,
    openErrorDetailsModal,
    copyErrorText,
    getActionTypeLabel,
    onImmediateSendTypeChange,
    isSystemTriggered,
  };
}

